import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { toast, Toaster } from "sonner";
import dayjs from "dayjs";
import { Eye, EyeOff, Loader2, Pencil, Trash2, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { ShadcnButton } from "./button";

export { Toaster };

const AUTH_FIELD_CLASS =
  "h-9 rounded-md border border-slate-200 bg-[#F3F6FA] px-3 text-[13px] text-slate-700 shadow-none placeholder:text-slate-400 focus-visible:outline-none focus-visible:border-[#1A3B70]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#1A3B70]/15 disabled:cursor-not-allowed disabled:opacity-50";

function getByPath(obj, path) {
  const parts = Array.isArray(path) ? path : [path];
  return parts.reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function setByPath(obj, path, value) {
  const parts = Array.isArray(path) ? path : [path];
  const clone = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const next = cur[key];
    cur[key] = Array.isArray(next) ? [...next] : { ...(next || {}) };
    cur = cur[key];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
}

function createFormInstance(initial = {}) {
  let values = { ...initial };
  const listeners = new Set();
  const rulesMap = new Map();
  const notify = () => listeners.forEach((fn) => fn(values));
  return {
    getFieldsValue: () => values,
    getFieldValue: (name) => getByPath(values, name),
    setFieldsValue: (patch) => {
      values = { ...values, ...patch };
      notify();
    },
    setFieldValue: (name, value, silent) => {
      values = setByPath(values, name, value);
      if (!silent) notify();
    },
    resetFields: () => {
      values = { ...initial };
      notify();
    },
    subscribe: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    setInitial: (init) => {
      initial = { ...init };
      values = { ...init };
      notify();
    },
    registerRules: (name, rules) => {
      if (name !== undefined && rules) rulesMap.set(String(name), { name, rules });
    },
    getRules: () => rulesMap,
  };
}

const FormContext = createContext(null);
const ListContext = createContext(null);

async function runRules(rules, value, getFieldValue) {
  for (const rule of rules || []) {
    const resolved = typeof rule === "function" ? rule({ getFieldValue }) : rule;
    if (!resolved) continue;
    if (resolved.required && (value === undefined || value === null || value === "")) {
      throw new Error(resolved.message || "This field is required");
    }
    if (resolved.type === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      throw new Error(resolved.message || "Please input valid email!");
    }
    if (resolved.min && String(value || "").length < resolved.min) {
      throw new Error(resolved.message || `Must be at least ${resolved.min} characters`);
    }
    if (resolved.validator) {
      await resolved.validator(resolved, value);
    }
  }
}

function Form({
  children,
  form,
  layout = "vertical",
  onFinish,
  onFinishFailed,
  initialValues,
  className,
  style,
  autoComplete,
}) {
  const inner = useRef(null);
  if (!inner.current) inner.current = form || createFormInstance(initialValues || {});
  const instance = form || inner.current;

  useEffect(() => {
    if (initialValues) instance.setFieldsValue(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors, setErrors] = useState({});
  const [tick, setTick] = useState(0);
  useEffect(() => instance.subscribe(() => setTick((n) => n + 1)), [instance]);

  const getFieldValue = instance.getFieldValue;
  const setFieldValue = instance.setFieldValue;

  const submit = async (e) => {
    e?.preventDefault?.();
    const values = instance.getFieldsValue();
    const nextErrors = {};
    for (const { name, rules } of instance.getRules().values()) {
      try {
        await runRules(rules, instance.getFieldValue(name), instance.getFieldValue);
      } catch (err) {
        nextErrors[String(name)] = err.message;
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      onFinishFailed?.({ values, errorFields: nextErrors });
      return;
    }
    onFinish?.(values);
  };

  const submitRef = useRef(submit);
  submitRef.current = submit;
  instance.submit = (event) => submitRef.current?.(event);

  const validateField = async (name, rules) => {
    try {
      await runRules(rules, instance.getFieldValue(name), getFieldValue);
      setErrors((prev) => {
        const next = { ...prev };
        delete next[String(name)];
        return next;
      });
      return true;
    } catch (err) {
      setErrors((prev) => ({ ...prev, [String(name)]: err.message }));
      return false;
    }
  };

  const ctx = {
    instance,
    values: instance.getFieldsValue(),
    errors,
    layout,
    getFieldValue,
    setFieldValue,
    validateField,
    tick,
  };

  const content =
    typeof children === "function"
      ? children({ getFieldValue, getFieldsValue: instance.getFieldsValue })
      : children;

  return (
    <FormContext.Provider value={ctx}>
      <form
        className={cn("w-full", className)}
        style={style}
        autoComplete={autoComplete}
        onSubmit={submit}
      >
        {content}
      </form>
    </FormContext.Provider>
  );
}

function FormItem({
  name,
  label,
  rules,
  children,
  valuePropName = "value",
  className,
  style,
  wrapperCol,
  labelCol,
}) {
  const form = useContext(FormContext);
  const listPrefix = useContext(ListContext);
  const fullName = useMemo(
    () =>
      name === undefined
        ? undefined
        : listPrefix != null
        ? Array.isArray(name)
          ? [listPrefix, ...name]
          : [listPrefix, name]
        : name,
    [listPrefix, name]
  );

  useEffect(() => {
    if (form?.instance && fullName !== undefined && rules) {
      form.instance.registerRules(fullName, rules);
    }
  }, [form, fullName, rules]);

  const value = fullName !== undefined ? form?.getFieldValue(fullName) : undefined;
  const error = form?.errors?.[String(fullName)];

  const control =
    fullName !== undefined && React.isValidElement(children)
      ? React.cloneElement(children, {
          [valuePropName]:
            valuePropName === "checked"
              ? !!value
              : value === undefined || value === null
              ? children.props[valuePropName] ?? ""
              : value,
          onChange: (...args) => {
            const e = args[0];
            let next = e;
            if (valuePropName === "checked") {
              next = e?.target ? e.target.checked : e;
            } else if (e?.target && (e.target.value !== undefined || e.target.type)) {
              next = e.target.type === "checkbox" ? e.target.checked : e.target.value;
            }
            form.setFieldValue(fullName, next);
            children.props.onChange?.(...args);
            if (rules) form.validateField(fullName, rules);
          },
        })
      : typeof children === "function"
      ? children({ getFieldValue: form.getFieldValue })
      : children;

  return (
    <div className={cn("mb-4 w-full", className)} style={style}>
      {label ? (
        <label className="mb-1 block text-sm font-medium text-foreground/80">{label}</label>
      ) : null}
      {control}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function FormList({ name, children, initialValue }) {
  const form = useContext(FormContext);
  const [, bump] = useState(0);
  const seeded = useRef(false);
  useEffect(() => {
    if (!form?.instance?.subscribe) return undefined;
    return form.instance.subscribe(() => bump((n) => n + 1));
  }, [form]);
  if (form && !seeded.current && initialValue && !form.getFieldValue(name)) {
    seeded.current = true;
    form.setFieldValue(name, initialValue, true);
  }
  const list = form.getFieldValue(name) || [];
  const fields = (Array.isArray(list) ? list : []).map((_, index) => ({
    key: index,
    name: index,
  }));
  const add = (item = {}) => {
    form.setFieldValue(name, [...(form.getFieldValue(name) || []), item]);
  };
  const remove = (index) => {
    const cur = [...(form.getFieldValue(name) || [])];
    cur.splice(index, 1);
    form.setFieldValue(name, cur);
  };
  return (
    <ListContext.Provider value={name}>
      {children(fields, { add, remove })}
    </ListContext.Provider>
  );
}

Form.Item = FormItem;
Form.List = FormList;
Form.useForm = () => {
  const ref = useRef(null);
  if (!ref.current) ref.current = createFormInstance();
  return [ref.current];
};
Form.useWatch = (name, form) => {
  const [value, setValue] = useState(() => form?.getFieldValue(name));
  useEffect(() => {
    if (!form) return undefined;
    return form.subscribe(() => setValue(form.getFieldValue(name)));
  }, [form, name]);
  return value;
};

const Button = React.forwardRef(function Button(
  {
    type = "default",
    htmlType = "button",
    size,
    loading,
    icon,
    danger,
    disabled,
    className,
    style,
    children,
    block,
    ...props
  },
  ref
) {
  const variant = danger
    ? "destructive"
    : type === "primary"
    ? "default"
    : type === "link"
    ? "link"
    : type === "text"
    ? "ghost"
    : "outline";
  const btnSize = size === "large" ? "lg" : size === "small" ? "sm" : "default";
  return (
    <ShadcnButton
      ref={ref}
      type={htmlType}
      variant={variant}
      size={btnSize}
      disabled={disabled || loading}
      className={cn(block && "w-full", className)}
      style={style}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </ShadcnButton>
  );
});

const Input = React.forwardRef(function Input(
  { size, prefix, suffix, className, style, allowClear, value, onChange, ...props },
  ref
) {
  const inputEl = (
    <input
      ref={ref}
      className={cn(
        "block w-full transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium",
        AUTH_FIELD_CLASS,
        prefix && "pl-9",
        suffix && "pr-9",
        className
      )}
      style={style}
      value={value ?? ""}
      onChange={onChange}
      {...props}
    />
  );
  if (!prefix && !suffix) return inputEl;
  return (
    <div className="relative flex w-full items-center">
      {prefix ? <span className="absolute left-3 z-10 text-muted-foreground">{prefix}</span> : null}
      {inputEl}
      {suffix ? <span className="absolute right-3 z-10 text-muted-foreground">{suffix}</span> : null}
    </div>
  );
});

const PasswordInput = React.forwardRef(function PasswordInput(props, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      ref={ref}
      {...props}
      type={visible ? "text" : "password"}
      suffix={
        <button type="button" onClick={() => setVisible((v) => !v)} className="text-muted-foreground">
          {visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
      }
    />
  );
});
Input.Password = PasswordInput;
Input.TextArea = React.forwardRef(function TextArea({ className, style, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-slate-200 bg-[#F3F6FA] px-3 py-2 text-[13px] text-slate-700 shadow-none placeholder:text-slate-400 focus-visible:outline-none focus-visible:border-[#1A3B70]/30 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#1A3B70]/15",
        className
      )}
      style={style}
      {...props}
    />
  );
});

function Checkbox({ checked, onChange, children, className, style, ...props }) {
  return (
    <label className={cn("inline-flex items-center gap-2 text-sm", className)} style={style}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-input text-primary"
        {...props}
      />
      {children}
    </label>
  );
}

function Select({
  options,
  children,
  value,
  onChange,
  placeholder,
  className,
  style,
  showSearch,
  onSearch,
  filterOption,
  loading,
  disabled,
  size,
  bordered = true,
  allowClear,
}) {
  const childOptions = React.Children.toArray(children)
    .filter(Boolean)
    .map((child) => ({
      value: child.props.value,
      label: child.props.children,
      disabled: child.props.disabled,
    }));
  const opts = options || childOptions;
  const [query, setQuery] = useState("");
  const filtered =
    showSearch && filterOption !== false && query
      ? opts.filter((o) => String(o.label ?? o.value).toLowerCase().includes(query.toLowerCase()))
      : opts;

  return (
    <div className={cn("relative w-full", className)} style={style}>
      {showSearch ? (
        <input
          className={cn(
            "block w-full",
            AUTH_FIELD_CLASS,
            bordered === false && "border-0 bg-transparent"
          )}
          disabled={disabled}
          placeholder={placeholder}
          value={value ?? query}
          onChange={(e) => {
            setQuery(e.target.value);
            onSearch?.(e.target.value);
            onChange?.(e.target.value);
          }}
        />
      ) : (
        <select
          className={cn(
            "block w-full",
            AUTH_FIELD_CLASS,
            bordered === false && "border-0 bg-transparent"
          )}
          disabled={disabled}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {filtered.map((opt) => (
            <option key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
      {loading ? (
        <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />
      ) : null}
    </div>
  );
}
Select.Option = function Option() {
  return null;
};

function DatePicker({ value, onChange, className, style, size, placeholder, format, min, max }) {
  const str = value ? dayjs(value).format("YYYY-MM-DD") : "";
  return (
    <input
      type="date"
      className={cn("block w-full", AUTH_FIELD_CLASS, className)}
      style={style}
      value={str}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => onChange?.(e.target.value ? dayjs(e.target.value) : null)}
    />
  );
}

function TimePicker({ value, onChange, className, style, size }) {
  const str = value ? dayjs(value).format("HH:mm") : "";
  return (
    <input
      type="time"
      className={cn("block w-full", AUTH_FIELD_CLASS, className)}
      style={style}
      value={str}
      onChange={(e) => onChange?.(e.target.value ? dayjs(`2000-01-01 ${e.target.value}`) : null)}
    />
  );
}

function Upload({ children, beforeUpload, showUploadList, className, style, accept }) {
  const inputRef = useRef(null);
  return (
    <div className={cn("inline-block cursor-pointer", className)} style={style} onClick={() => inputRef.current?.click()}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) beforeUpload?.(file);
          e.target.value = "";
        }}
      />
      {children}
    </div>
  );
}

function Avatar({ size = 32, src, icon, children, className, style }) {
  const dim = typeof size === "number" ? size : 32;
  return (
    <AvatarPrimitive.Root
      className={cn("inline-flex items-center justify-center overflow-hidden rounded-full bg-muted", className)}
      style={{ width: dim, height: dim, ...style }}
    >
      {src ? (
        <AvatarPrimitive.Image src={src} className="h-full w-full object-cover" />
      ) : (
        <AvatarPrimitive.Fallback className="flex h-full w-full items-center justify-center text-xs">
          {icon || children || "A"}
        </AvatarPrimitive.Fallback>
      )}
    </AvatarPrimitive.Root>
  );
}

function Image({ src, alt, preview = true, width, height, className, style }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <img
        src={src}
        alt={alt || ""}
        width={width}
        height={height}
        className={className}
        style={{ maxWidth: "100%", cursor: preview !== false ? "zoom-in" : undefined, ...style }}
        onClick={(event) => {
          event.stopPropagation();
          if (preview !== false) setOpen(true);
        }}
      />
      {open && preview !== false
        ? createPortal(
            <div
              className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/75 p-6"
              onClick={() => setOpen(false)}
              role="dialog"
              aria-modal="true"
              aria-label={alt || "Image preview"}
            >
              <button
                type="button"
                className="absolute top-5 right-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg"
                onClick={() => setOpen(false)}
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
              <img
                src={src}
                alt={alt || ""}
                className="max-h-[86vh] max-w-[min(92vw,880px)] rounded-2xl bg-white p-8 object-contain shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              />
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function Modal({
  open,
  visible,
  onOk,
  onCancel,
  okText = "OK",
  cancelText = "Cancel",
  title,
  children,
  className,
  style,
  okButtonProps,
  cancelButtonProps,
  footer,
}) {
  const isOpen = open ?? visible ?? false;
  const {
    left: _left,
    right: _right,
    top: _top,
    bottom: _bottom,
    marginLeft: _ml,
    marginRight: _mr,
    transform: _transform,
    ...safeStyle
  } = style || {};
  return (
    <Dialog.Root open={isOpen} onOpenChange={(next) => !next && onCancel?.()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1000] bg-black/40" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-[1001] w-[90vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg outline-none",
            className
          )}
          style={safeStyle}
        >
          {title ? <Dialog.Title className="mb-3 text-lg font-semibold">{title}</Dialog.Title> : <Dialog.Title className="sr-only">Dialog</Dialog.Title>}
          <div>{children}</div>
          {footer === null ? null : (
            <div className="mt-6 flex justify-end gap-2">
              {Array.isArray(footer) ? (
                footer
              ) : (
                <>
                  <Button onClick={onCancel} style={cancelButtonProps?.style} className={cancelButtonProps?.className}>
                    {cancelText}
                  </Button>
                  <Button type="primary" onClick={onOk} style={okButtonProps?.style} className={okButtonProps?.className}>
                    {okText}
                  </Button>
                </>
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function menuItemText(label) {
  if (typeof label === "string") return label.trim();
  const kids = label?.props?.children;
  if (typeof kids === "string") return kids.trim();
  return "";
}

function menuItemDanger(item, text) {
  if (item?.danger) return true;
  const color = item?.label?.props?.style?.color;
  return color === "#e83038" || color === "#dc2626" || text.toLowerCase() === "delete";
}

const MENU_ICONS = {
  view: Eye,
  edit: Pencil,
  delete: Trash2,
};

function Dropdown({ menu, children }) {
  const items = menu?.items || [];
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="data-more-menu z-[1050]"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {items.map((item, index) => {
            const text = menuItemText(item.label);
            const danger = menuItemDanger(item, text);
            const Icon = MENU_ICONS[text.toLowerCase()];
            const simple = Boolean(text) && typeof (item.label?.props?.children ?? item.label) === "string";
            return (
              <React.Fragment key={item.key || index}>
                {danger && index > 0 ? <DropdownMenu.Separator className="data-more-sep" /> : null}
                <DropdownMenu.Item
                  className={cn("data-more-item", danger && "is-danger")}
                  onSelect={(event) => {
                    const handler = item.onClick || item.label?.props?.onClick;
                    handler?.(event);
                  }}
                >
                  {simple ? (
                    <>
                      {Icon ? <Icon size={15} strokeWidth={2} /> : null}
                      <span>{text}</span>
                    </>
                  ) : (
                    item.label
                  )}
                </DropdownMenu.Item>
              </React.Fragment>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function Popover({ content, children, trigger = "click", open, onOpenChange, placement = "bottomRight" }) {
  const side = String(placement).startsWith("top")
    ? "top"
    : String(placement).startsWith("left")
    ? "left"
    : String(placement).startsWith("right")
    ? "right"
    : "bottom";
  const align = String(placement).toLowerCase().includes("left")
    ? "start"
    : String(placement).toLowerCase().includes("right")
    ? "end"
    : "center";
  return (
    <PopoverPrimitive.Root modal={false} open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={10}
          collisionPadding={16}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="bw-popover-panel z-[1050] rounded-2xl border-0 p-0 shadow-lg outline-none"
        >
          {content}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function Drawer({
  open,
  onClose,
  children,
  placement = "right",
  className,
  title,
  width = 400,
  closable = true,
  footer,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className={cn("bw-drawer", className)}>
      <button type="button" className="bw-drawer-backdrop" aria-label="Close drawer" onClick={onClose} />
      <aside
        className={cn("bw-drawer-panel", placement === "left" && "is-left")}
        style={{ width: `min(${typeof width === "number" ? `${width}px` : width}, calc(100vw - 12px))` }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Drawer"}
      >
        {(title || closable) && (
          <div className="bw-drawer-head">
            {title ? <h2>{title}</h2> : <span />}
            {closable ? (
              <button type="button" className="bw-drawer-close" onClick={onClose} aria-label="Close">
                <X size={18} />
              </button>
            ) : null}
          </div>
        )}
        <div className="bw-drawer-body">{children}</div>
        {footer ? <div className="bw-drawer-foot">{footer}</div> : null}
      </aside>
    </div>,
    document.body
  );
}

function Sider({ children, width = 200, className, style, breakpoint, onBreakpoint }) {
  useEffect(() => {
    if (!breakpoint || !onBreakpoint) return undefined;
    const mq = { lg: 992, md: 768, sm: 576, xl: 1200 }[breakpoint] || 992;
    const check = () => onBreakpoint(window.innerWidth < mq);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint, onBreakpoint]);
  return (
    <aside className={cn("ant-layout-sider", className)} style={{ width, minWidth: width, ...style }}>
      <div className="ant-layout-sider-children h-full">{children}</div>
    </aside>
  );
}
Sider.displayName = "Sider";

function Header({ children, className, style }) {
  return (
    <header className={cn("ant-layout-header", className)} style={style}>
      {children}
    </header>
  );
}
function Content({ children, className, style }) {
  return (
    <main className={cn("ant-layout-content flex-1", className)} style={style}>
      {children}
    </main>
  );
}

function Layout({ children, className, style, hasSider }) {
  const kids = React.Children.toArray(children);
  const sider = hasSider ?? kids.some((child) => child?.type?.displayName === "Sider");
  return (
    <div className={cn("flex w-full", sider ? "flex-row" : "flex-col", className)} style={style}>
      {children}
    </div>
  );
}
Layout.Header = Header;
Layout.Content = Content;
Layout.Sider = Sider;

function Menu({ items = [], selectedKeys = [], className, style }) {
  const selected = selectedKeys.map(String);
  return (
    <nav className={cn("ant-menu flex w-full flex-col", className)} style={style}>
      {items.map((item) => (
        <div
          key={item.key}
          role="button"
          className={cn(
            "ant-menu-item flex cursor-pointer items-center gap-2",
            selected.includes(String(item.key)) && "ant-menu-item-selected"
          )}
          onClick={item.onClick}
        >
          {item.icon}
          <span>{item.label}</span>
        </div>
      ))}
    </nav>
  );
}

function Row({ children, className, style, justify, align, gutter }) {
  const gap = Array.isArray(gutter) ? gutter[0] : gutter;
  return (
    <div
      className={cn("ui-row", className)}
      style={{
        justifyContent: justify,
        alignItems: align,
        gap: gap ? `${gap}px` : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Col({ xs, sm, md, lg, xl, span, className, style, children, offset, ...rest }) {
  const base = xs !== undefined ? xs : span !== undefined ? span : 24;
  const classes = [
    "ui-col",
    `col-xs-${base}`,
    sm !== undefined ? `col-sm-${sm}` : "",
    md !== undefined ? `col-md-${md}` : "",
    lg !== undefined ? `col-lg-${lg}` : "",
    xl !== undefined ? `col-xl-${xl}` : "",
    className,
  ];
  return (
    <div className={cn(classes)} style={style} {...rest}>
      {children}
    </div>
  );
}

function Table({ dataSource = [], columns = [], pagination, loading, rowKey, className }) {
  const rows = dataSource || [];
  const keyOf = (row, i) =>
    typeof rowKey === "function" ? rowKey(row) : row?.[rowKey] || row?._id || row?.key || i;
  if (loading) return <Skeleton active />;
  return (
    <div className={cn("w-full overflow-auto", className)}>
      <table className="ant-table w-full border-collapse text-left text-sm">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key || col.dataIndex} className="ant-table-cell border-b px-3 py-2 font-semibold">
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={keyOf(row, i)} className="ant-table-row">
              {columns.map((col) => {
                const val = col.dataIndex ? row[col.dataIndex] : row;
                return (
                  <td key={col.key || col.dataIndex} className="ant-table-cell border-b px-3 py-2">
                    {col.render ? col.render(val, row, i) : val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ current = 1, pageSize = 10, total = 0, onChange, showSizeChanger, onShowSizeChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center gap-2">
      {showSizeChanger ? (
        <select
          className="h-8 rounded border px-2 text-sm"
          value={pageSize}
          onChange={(e) => onShowSizeChange?.(1, Number(e.target.value))}
        >
          {[10, 20, 30, 40, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      ) : null}
      {Array.from({ length: pages }, (_, i) => i + 1)
        .slice(Math.max(0, current - 3), current + 2)
        .map((page) => (
          <button
            key={page}
            type="button"
            className={cn(
              "h-8 min-w-8 rounded border px-2 text-sm",
              page === current && "bg-primary text-primary-foreground"
            )}
            onClick={() => onChange?.(page, pageSize)}
          >
            {page}
          </button>
        ))}
    </div>
  );
}

function Skeleton({ active, children, rows, paragraph, variant }) {
  const count = rows || paragraph?.rows || 6;
  const mode = variant || (paragraph ? "lines" : "list");

  return (
    <div
      className={cn("bw-skeleton", active && "is-active", mode === "list" && "is-list")}
      aria-hidden="true"
    >
      {children ||
        (mode === "list"
          ? Array.from({ length: count }, (_, index) => (
              <div className="bw-skeleton-row" key={index}>
                <span className="bw-skeleton-avatar" />
                <span className="bw-skeleton-copy">
                  <span className="bw-skeleton-line is-title" />
                  <span className="bw-skeleton-line is-short" />
                </span>
              </div>
            ))
          : Array.from({ length: count }, (_, index) => (
              <span
                className={cn(
                  "bw-skeleton-line",
                  index === 0 && "is-title",
                  index % 3 === 2 && "is-short"
                )}
                key={index}
              />
            )))}
    </div>
  );
}

function Spin({ spinning = true, children }) {
  if (!spinning && children) return children;
  return (
    <div className="relative">
      {children}
      {spinning ? (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : null}
    </div>
  );
}

function Alert({ message, type = "info", style, className }) {
  const colors = {
    success: "bg-green-50 text-green-700",
    error: "bg-red-50 text-red-700",
    warning: "bg-yellow-50 text-yellow-700",
    info: "bg-blue-50 text-blue-700",
  };
  return (
    <div className={cn("rounded px-3 py-1 text-sm", colors[type] || colors.info, className)} style={style}>
      {message}
    </div>
  );
}

function Divider({ style, className }) {
  return <hr className={cn("my-3 border-border", className)} style={style} />;
}

function Tag({ children, closable, onClose, className, style, icon }) {
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-sm", className)}
      style={style}
    >
      {icon}
      {children}
      {closable ? (
        <button type="button" onClick={onClose}>
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}

function Tabs({ items, defaultActiveKey, children, className }) {
  const panes = items || React.Children.toArray(children).map((child) => ({
    key: child.props.tabKey || child.key,
    label: child.props.tab,
    children: child.props.children,
  }));
  const [active, setActive] = useState(defaultActiveKey || panes[0]?.key);
  return (
    <div className={className}>
      <div className="mb-3 flex gap-2 border-b">
        {panes.map((pane) => (
          <button
            key={pane.key}
            type="button"
            className={cn("px-3 py-2 text-sm", String(active) === String(pane.key) && "border-b-2 border-primary")}
            onClick={() => setActive(pane.key)}
          >
            {pane.label}
          </button>
        ))}
      </div>
      {panes.find((pane) => String(pane.key) === String(active))?.children}
    </div>
  );
}
Tabs.TabPane = function TabPane({ children }) {
  return children;
};

function List({ dataSource = [], renderItem, header, className }) {
  return (
    <div className={className}>
      {header}
      {(dataSource || []).map((item, i) => (
        <div key={item?.key || i}>{renderItem ? renderItem(item, i) : item}</div>
      ))}
    </div>
  );
}
List.Item = function ListItem({ children, extra }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>{children}</div>
      {extra}
    </div>
  );
};

function Badge({ count, children, className }) {
  return (
    <span className={cn("relative inline-flex", className)}>
      {children}
      {count ? (
        <span className="absolute -right-1 -top-1 rounded-full bg-destructive px-1 text-[10px] text-white">
          {count}
        </span>
      ) : null}
    </span>
  );
}

function Breadcrumb({ items, children }) {
  return <div className="text-sm text-muted-foreground">{children || items?.map((i) => i.title).join(" / ")}</div>;
}

const Typography = {
  Title: function Title({ children, level = 2, className, style }) {
    const Tag = `h${Math.min(Math.max(level, 1), 6)}`;
    return (
      <Tag className={className} style={style}>
        {children}
      </Tag>
    );
  },
  Text: function Text({ children, className, style }) {
    return (
      <span className={className} style={style}>
        {children}
      </span>
    );
  },
  Paragraph: function Paragraph({ children, className, style }) {
    return (
      <p className={className} style={style}>
        {children}
      </p>
    );
  },
};

const message = {
  success: (text) => toast.success(typeof text === "string" ? text : text?.content || "Success"),
  error: (text) => toast.error(typeof text === "string" ? text : text?.content || "Error"),
  warning: (text) => toast.warning(typeof text === "string" ? text : text?.content || "Warning"),
  info: (text) => toast(typeof text === "string" ? text : text?.content || "Info"),
};

const theme = {
  useToken: () => ({ token: { colorPrimary: "#2f58aa" } }),
};

function InputNumber({ className, style, size, value, onChange, min, max, ...props }) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      className={cn("flex w-full", AUTH_FIELD_CLASS, className)}
      style={style}
      value={value ?? ""}
      onChange={(e) => {
        const n = e.target.value === "" ? null : Number(e.target.value);
        onChange?.(n);
      }}
      {...props}
    />
  );
}

function Card({ children, title, className, style }) {
  return (
    <div className={cn("rounded-lg border bg-card p-4 shadow-sm", className)} style={style}>
      {title ? <div className="mb-3 font-semibold">{title}</div> : null}
      {children}
    </div>
  );
}

function Space({ children, size = "small", className, style }) {
  const gap = size === "large" || size === "middle" ? "0.75rem" : "0.5rem";
  return (
    <div className={cn("inline-flex flex-wrap items-center", className)} style={{ gap, ...style }}>
      {children}
    </div>
  );
}

function Link({ children, href = "#", className, style, onClick }) {
  return (
    <a href={href} className={cn("text-primary underline-offset-4 hover:underline", className)} style={style} onClick={onClick}>
      {children}
    </a>
  );
}

const TextArea = Input.TextArea;
const Item = List.Item;

function ConfigProvider({ children }) {
  return children;
}

export {
  Layout,
  Menu,
  Drawer,
  Image,
  Avatar,
  Dropdown,
  Popover,
  Alert,
  Modal,
  Input,
  Row,
  Col,
  Button,
  Typography,
  message,
  Form,
  Select,
  Spin,
  TimePicker,
  Upload,
  Table,
  Skeleton,
  Checkbox,
  Tabs,
  Divider,
  DatePicker,
  Tag,
  List,
  Pagination,
  Badge,
  Breadcrumb,
  theme,
  ConfigProvider,
  Header,
  Content,
  Sider,
  InputNumber,
  Card,
  Space,
  TextArea,
  Item,
  Link,
};
